/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IPivotStyleProps, IPivotStyles, Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import { QnaMakerQnaList } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaList';
import { QnaMakerSynonymList } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymList';

type QnaMakerLeftSidePaneProps = {
    activeTab: string;
    setWidth: (width: string) => void;
    isCommandBarInExpandedState: boolean;
    qnaUiStore: QnaMakerQnaPairsUiStore;
    synonymUiStore: QnaMakerSynonymsUiStore;
    setMultiSelectMode: (state: boolean) => void;
};

const messages = defineMessages({
    expandLeftSidePane: { id: 'qnaMaker.qnaMakerLeftSidePane.expandLeftSidePane', defaultMessage: 'Expand left side pane' },
    collapseLeftSidePane: {
        id: 'qnaMaker.qnaMakerLeftSidePane.collapseLeftSidePane',
        defaultMessage: 'Collapse left side pane'
    },
    questions: {
        id: 'qnaMaker.qnaMakerLeftSidePane.questions',
        defaultMessage: 'Question answer pairs'
    },
    synonyms: {
        id: 'qnaMaker.qnaMakerLeftSidePane.synonyms',
        defaultMessage: 'Synonyms'
    }
});

const theme = getScopedTheme('QnaMakerLeftSidePane');

const collapseButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { width: 44, height: 44 }
});

const collapseIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: 10, color: theme.collapseIconColor }
});

const { getClassName } = getStylist('QnaMakerLeftSidePane');
const pivotRootClassName = getClassName('PivotRoot', {
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    flex: 1,
    height: '100%'
});
const pivotItemClassName = getClassName('PivotItem', { height: '100%' });
const pivotStyles = classNamesFunction<IPivotStyleProps, IPivotStyles>()({
    root: { marginBottom: 15, display: 'flex', flexWrap: 'wrap' },
    itemContainer: { overflow: 'hidden', height: '100%' }
});

export const QnaMakerLeftSidePane = observer((props: QnaMakerLeftSidePaneProps) => {
    const { activeTab, qnaUiStore, synonymUiStore, setWidth, isCommandBarInExpandedState, setMultiSelectMode } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const history = useHistory();
    const currentProjectName = rootStore.currentQnaMakerProjectStore?.name;

    const onPivotClick = (item: PivotItem) => {
        logPivotClick(serviceName, 'NewEditKb', item.props.itemKey, rootStore.currentQnaMakerProjectStore.id);
        history.push(`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=${item.props.itemKey}`);
    };

    React.useEffect(() => {
        if (isCollapsed) {
            setWidth('44px');
        } else {
            setWidth('400px');
        }
    }, [isCollapsed]);

    const [displayedQnasCount, setDisplayedQnasCount] = React.useState(qnaUiStore.displayedQnaPairStores.length);
    const [displayedSynonymsCount, setDisplayedSynonymsCount] = React.useState(synonymUiStore.displayedSynonymStores.length);

    const iconStackStyles = mergeStyleSets({
        root: {
          '@media (max-height: 370px)': {
            position: 'absolute',
            right: 0
          }
        }
      });

    return (
        <Stack verticalFill>
            <Stack horizontalAlign="end" styles={iconStackStyles}>
                <IconButton
                    iconProps={{
                        iconName: isCollapsed ? 'DoubleChevronLeftMedMirrored' : 'DoubleChevronLeftMed',
                        styles: collapseIconStyles
                    }}
                    styles={collapseButtonStyles}
                    aria-label={isCollapsed ? localize(messages.expandLeftSidePane) : localize(messages.collapseLeftSidePane)}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                />
            </Stack>

            {!isCollapsed && (
                <Pivot styles={pivotStyles} selectedKey={activeTab} className={pivotRootClassName} onLinkClick={onPivotClick}>
                    <PivotItem
                        itemKey="qnas"
                        itemIcon="TaskGroup"
                        className={pivotItemClassName}
                        itemCount={displayedQnasCount}
                        headerText={localize(messages.questions)}>
                        <QnaMakerQnaList
                            qnaUiStore={qnaUiStore}
                            setMultiSelectMode={setMultiSelectMode}
                            setQnasCount={count => setDisplayedQnasCount(count)}
                            isCommandBarInExpandedState={isCommandBarInExpandedState}
                        />
                    </PivotItem>
                    <PivotItem
                        itemKey="synonyms"
                        itemIcon="TextField"
                        className={pivotItemClassName}
                        itemCount={displayedSynonymsCount}
                        headerText={localize(messages.synonyms)}>
                        <QnaMakerSynonymList synonymUiStore={synonymUiStore} setSynonymsCount={count => setDisplayedSynonymsCount(count)} />
                    </PivotItem>
                </Pivot>
            )}
        </Stack>
    );
});
